import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useTranslation } from "../context/TranslationContext";
import { useWebSocket } from '../context/WebSocketContext';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { ConnectionEventEmitter } from '../utils/ConnectionEventEmitter';
import log from "loglevel";

import PanelMain from '../components/PanelMain';
import PanelCarousel from "../components/PanelCarousel";
import Button from '../components/Button';
import Input from '../components/Input';
import PhoneInput from 'react-phone-input-2';

import {
    validateEmail,
    validatePhone,
    validateName
} from "../utils/Validation";
  
import '../styles/WaitingList.css'; 

import { ReactComponent as Logo } from "../resources/assets/logoHorizontal.svg"; 
import { ReactComponent as MainFinance } from "../resources/assets/login/finance.svg";
import { ReactComponent as MainGuests } from "../resources/assets/login/guests.svg";
import { ReactComponent as MainOffers } from "../resources/assets/login/offers.svg";
import { ReactComponent as MainProperties } from "../resources/assets/login/properties.svg";
import { ReactComponent as MainReservations } from "../resources/assets/login/reservations.svg";
import { ReactComponent as MainMarketing } from "../resources/assets/login/marketing.svg";
import { ReactComponent as MainStatistics } from "../resources/assets/login/statistics.svg";

function WaitingList() {
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    const { waitingList } = useAuth();
    const [buttonStatus, setButtonStatus] = useState('inactive');
    const [formData, setFormData] = useState({
        email: '',
        phone: '',
        userName: '',
        userSurname: ''
    });
    const [added, setAdded] = useState(false);

    const inputRefs = {
        enterName: useRef(null),
        enterSurname: useRef(null),
        enterPhone: useRef(null),
        enterEmail: useRef(null)
    };

    const items = [
        { icon: MainProperties, label: t.login.PropertiesTitle, description: t.login.PropertiesDescription},
        { icon: MainGuests, label: t.login.GuestsTitle, description: t.login.GuestsDescription},
        { icon: MainOffers, label: t.login.OffersTitle, description: t.login.OffersDesctiption},
        { icon: MainReservations, label: t.login.ReservationsTitle, description: t.login.ReservationsDescription},
        { icon: MainMarketing, label: t.login.MarketingTitle, description: t.login.MarketingDescription},
        { icon: MainFinance, label: t.login.FinanceTitle, description: t.login.FinanceDescription},
        { icon: MainStatistics, label: t.login.StatisticsTitle, description:  t.login.StatisticsDescription}
    ];
  
    const resetFormState = useCallback(() => {
        setFormData({
            email: '',
            phone: '',
            userName: '',
            userSurname: ''
        });
        setButtonStatus('inactive');
    }, []);

    const handleWaitingListSuccess = useCallback(() => {
        setAdded(true);
        resetFormState();
    }, [resetFormState]);

    const handleWaitingListError = useCallback(() => {
        setAdded(false);
        showNotification(t.waitingList.AlreadyExists);
        setButtonStatus('priority');
    }, [showNotification, t.waitingList.AlreadyExists]);

    const connectionReceived = useCallback((receivedMessage) => {
        if (!receivedMessage) {
            return;
        }

        if (receivedMessage.area !== 'user' || receivedMessage.command !== 'waitingList') {
            return;
        }

        if (receivedMessage.status === 'ok') {
            handleWaitingListSuccess();
        } else {
            handleWaitingListError();
        }
    }, [handleWaitingListSuccess, handleWaitingListError, showNotification, t.general.ErrorOccurred]);

    const refreshFocus = (state) => {
        setTimeout(() => {
            const ref = inputRefs[state];
            if (ref && ref.current) {
                ref.current.focus();
            }
        }, 100); 
    };


    useEffect(() => {    
        refreshFocus("enterName");
        ConnectionEventEmitter.on('received', connectionReceived);
        return () => { 
            ConnectionEventEmitter.off('received', connectionReceived);
        }
    }, []);


    const handleChange = (field, value) => {
        if (buttonStatus !== "loading") {
            const newFormData = {
                ...formData,
                [field]: value
            };
            setFormData(newFormData);

            if (validateName(newFormData.userName, newFormData.userSurname) && 
                validatePhone(newFormData.phone) && 
                validateEmail(newFormData.email)) {
                setButtonStatus("priority");
            } else {
                setButtonStatus("inactive");
            }
        }
    };

    const sign = () => {
        if(buttonStatus !== "loading") {
            setButtonStatus("loading");
            waitingList(
                formData.email,
                formData.phone,
                formData.userName,
                formData.userSurname
            );
        }
    };  

  return (
    <div className="waiting-list-container">
        <Logo className="waiting-list-logo" />
        <PanelMain className="waiting-list-main">
            <PanelCarousel items={items} />

            <div className="waiting-list-panel">
                {added===false ? (
                    <>
                        <div className="waiting-list-title">{t.waitingList.Title}</div>
                        <Input 
                            placeholder={t.general.EnterNameHint} 
                            onChange={(value) => handleChange('userName', value)} 
                            onEnter={() => refreshFocus("enterSurname")} 
                            value={formData.userName} 
                            minLength="2" 
                            maxLength="128" 
                            ref={inputRefs.enterName} 
                            disabled={buttonStatus === 'loading'} 
                        /> 
                        <Input 
                            placeholder={t.general.EnterSurnameHint} 
                            onChange={(value) => handleChange('userSurname', value)} 
                            onEnter={() => refreshFocus("enterPhone")} 
                            value={formData.userSurname} 
                            minLength="2" 
                            maxLength="128" 
                            ref={inputRefs.enterSurname} 
                            disabled={buttonStatus === 'loading'} 
                        /> 
                        <PhoneInput
                            placeholder={t.general.PhoneHint}
                            value={formData.phone}
                            inputProps={{
                                onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        refreshFocus("enterEmail");
                                    }
                                },
                                disabled: buttonStatus === 'loading',
                                ref: inputRefs.enterPhone, 
                            }}
                            onChange={(value) => handleChange('phone', value)}
                        />        
                        <Input 
                            placeholder={t.general.EmailHint} 
                            onChange={(value) => handleChange('email', value)} 
                            onEnter={() => sign()} 
                            value={formData.email} 
                            minLength="5" 
                            maxLength="128" 
                            ref={inputRefs.enterEmail} 
                            disabled={buttonStatus === 'loading'} 
                        /> 
                        <Button type={buttonStatus} onClick={() => sign()}>{t.waitingList.Sign}</Button>
                        <div className="waiting-list-agreement">{t.waitingList.Agreement}</div>
                    </>
                ) : (
                    <>
                        <div className="waiting-list-title">{t.waitingList.Added}</div>
                        <Button type="priority" onClick={() => setAdded(false)}>{t.general.Back}</Button>
                    </>
                )}

                
            </div>
        </PanelMain>
    </div>
  );
}

export default WaitingList;